import { useState, useEffect, useCallback } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { api, HEALTH_POLL_CONFIG } from '../shared/api/client';
// Контракт из OpenAPI (camelCase): ручной snake_case-тип расходился с API — в подвале «—».
import type { SystemResources } from '../shared/api/generated';
import { clearApiAuthLost } from '../shared/auth/apiAuthSession';
import { isNetworkError, logError, logWarn, WS_CHANNELS } from '../shared/config/constants';
import { useBusChannel } from '../shared/ws/useBusSocket';

interface HealthStatus {
  status: 'healthy' | 'not_ready' | 'degraded';
  timestamp: string;
  version: string;
  service: string;
}

interface ReadinessStatus {
  status: 'ready' | 'not_ready';
  checks: {
    database: boolean;
    redis: boolean;
    kafka: boolean;
    binance_api: boolean;
  };
  errors: string[];
  timestamp: string;
}

interface LivenessStatus {
  status: 'alive' | 'degraded';
  latency_ms: number;
  timestamp: string;
}

interface SystemHealth {
  health: HealthStatus | null;
  readiness: ReadinessStatus | null;
  liveness: LivenessStatus | null;
  resources: SystemResources | null;
  isLoading: boolean;
  /** Любая ошибка health/liveness/readiness/resources (backward compat). */
  error: Error | null;
  /** API недоступен (healthz/liveness). */
  apiUnavailable: boolean;
  healthError: Error | null;
  resourcesError: Error | null;
}

export const useSystemHealth = (): SystemHealth => {
  const [latency, setLatency] = useState<number>(0);
  const queryClient = useQueryClient();
  type UnknownRecord = Record<string, unknown>;
  const isRecord = (v: unknown): v is UnknownRecord => !!v && typeof v === 'object' && !Array.isArray(v);

  // WebSocket подписка на обновления здоровья системы
  useBusChannel(
    WS_CHANNELS.SYSTEM_HEALTH(),
    useCallback((event: unknown) => {
      try {
        const payload = (event as { payload?: unknown; data?: unknown })?.payload ?? (event as { data?: unknown })?.data ?? event;
        if (payload && typeof payload === 'object') {
          const healthUpdate = payload as Partial<HealthStatus>;
          // Обновляем кэш здоровья системы
          queryClient.setQueryData(['health'], (oldHealth: HealthStatus | null) => {
            if (!oldHealth) return oldHealth;
            return {
              ...oldHealth,
              ...healthUpdate,
            };
          });
        }
      } catch (error) {
        logError('Failed to process system health update', error);
      }
    }, [queryClient]),
    { enabled: true, snapshot: false }
  );

  // Health check - базовая проверка
  const { data: health = null, isLoading: healthLoading, error: healthError } = useQuery<HealthStatus>({
    queryKey: ['health'],
    queryFn: async () => {
      const response = await api.get<HealthStatus>('/api/health/healthz', HEALTH_POLL_CONFIG);
      return response.data;
    },
    refetchInterval: 30000, // каждые 30 сек
    retry: (failureCount, error) => (isNetworkError(error) ? false : failureCount < 2),
    retryDelay: (failureCount, error) => (isNetworkError(error) ? 0 : Math.min(2000 * 2 ** failureCount, 15000)),
  });

  // Readiness check - проверка компонентов
  const { data: readiness = null, isLoading: readinessLoading, error: readinessError } = useQuery<ReadinessStatus>({
    queryKey: ['readiness'],
    queryFn: async () => {
      const response = await api.get<ReadinessStatus>('/api/health/readyz', HEALTH_POLL_CONFIG);
      return response.data;
    },
    refetchInterval: 30000, // каждые 30 сек
    retry: (failureCount: number, error: unknown) => {
      // Не retry на 503/500 ошибках readiness - это нормально если компоненты не готовы
      if (error && (error as AxiosError)?.response?.status && (error as AxiosError).response!.status >= 500) return false;
      return failureCount < 2;
    },
    retryDelay: 2000,
  });

  // Liveness check - используем healthz как простой liveness check
  const { data: liveness, isLoading: livenessLoading, error: livenessError } = useQuery<LivenessStatus>({
    queryKey: ['liveness'],
    queryFn: async () => {
      await api.get('/api/health/healthz', HEALTH_POLL_CONFIG);
      return {
        status: 'alive' as const,
        latency_ms: latency,
        timestamp: new Date().toISOString()
      } as LivenessStatus;
    },
    refetchInterval: 15000,
    retry: (failureCount, error) => (isNetworkError(error) ? false : failureCount < 1),
    retryDelay: 5000,
  });

  // System resources - реальный API endpoint
  const { data: resources = null, isLoading: resourcesLoading, error: resourcesError } = useQuery<SystemResources>({
    queryKey: ['system-resources'],
    queryFn: async () => {
      try {
        const response = await api.get<SystemResources>('/api/health/system/resources', HEALTH_POLL_CONFIG);
        return response.data;
      } catch (error) {
        if (!isNetworkError(error)) {
          logError('Failed to fetch system resources', {
            message: isRecord(error) && typeof error.message === 'string' ? error.message : undefined,
            status: isRecord(error) && isRecord(error.response) ? (error.response as UnknownRecord).status : undefined,
            url: '/api/health/system/resources'
          });
        }
        throw error;
      }
    },
    refetchInterval: 10000,
    retry: (failureCount, error) => {
      if (isNetworkError(error)) return false;
      if (isRecord(error) && isRecord(error.response) && (error.response as UnknownRecord).status) {
        const status = Number((error.response as UnknownRecord).status);
        if (status === 404 || status >= 500) {
          logWarn(`System resources endpoint returned ${status}, skipping retry`);
          return false;
        }
      }
      return failureCount < 3;
    },
    retryDelay: (failureCount) => Math.min(3000 * 2 ** failureCount, 15000),
  });

  // Измерение latency (не спамим при недоступности API)
  useEffect(() => {
    const measureLatency = async () => {
      const start = performance.now();
      try {
        await api.get('/api/health/healthz', HEALTH_POLL_CONFIG);
        setLatency(Math.round(performance.now() - start));
      } catch {
        setLatency(0);
      }
    };

    measureLatency();
    const interval = setInterval(measureLatency, 20000);
    return () => clearInterval(interval);
  }, []);

  const connectivityError = healthError || livenessError || null;
  const apiUnavailable = Boolean(connectivityError);

  useEffect(() => {
    if (health?.status === 'healthy') {
      clearApiAuthLost();
    }
  }, [health?.status]);

  return {
    health,
    readiness,
    liveness: liveness ? { ...liveness, latency_ms: latency || liveness.latency_ms } : null,
    resources: resources as SystemResources | null,
    isLoading: healthLoading || readinessLoading || livenessLoading || resourcesLoading,
    error: connectivityError || readinessError || resourcesError || null,
    apiUnavailable,
    healthError: connectivityError,
    resourcesError: resourcesError || null,
  };
};
